<template>
  <div class="certification-area-chart">
    <div class="chart-header">
      <span class="chart-title">Évolution des surfaces par certification</span>
    </div>
    <div v-if="loading" class="chart-loading">
      <i class="el-icon-loading" />
      <p>Chargement des données...</p>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-warning" />
      <p>Aucune donnée disponible pour la période sélectionnée</p>
    </div>
    <div v-else ref="chartContainer" class="chart-container">
      <Bubble
        :chart-data="chartData"
        :chart-options="chartOptions"
        :height="chartHeight || null"
      />
    </div>
  </div>
</template>

<script>
import { Bubble } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { mapGetters } from 'vuex';
import DashboardAPI from '@/api/dashboard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'CertificationAreaChart',
  components: {
    Bubble,
  },
  data() {
    return {
      loading: false,
      chartData: {
        labels: [],
        datasets: [],
      },
      chartHeight: null,
    };
  },
  computed: {
    ...mapGetters('dashboard', ['filters']),
    ...mapGetters('camp_agricole', ['camp_id']),
    hasData() {
      return this.chartData &&
             this.chartData.labels &&
             this.chartData.labels.length > 0 &&
             this.chartData.datasets &&
             this.chartData.datasets.length > 0 &&
             this.chartData.datasets[0].data &&
             this.chartData.datasets[0].data.length > 0;
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          title: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title(context) {
                return context[0].raw.label || '';
              },
              label(context) {
                const point = context.raw;
                return [
                  `${context.dataset.label}`,
                  `Surface: ${point.y.toFixed(2)} ha`,
                  `Période: ${point.label || ''}`,
                ];
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Surface (hectares)',
            },
            ticks: {
              callback(value) {
                return `${value} ha`;
              },
            },
          },
          x: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Période',
            },
            ticks: {
              stepSize: 1,
              callback: (value) => {
                const labels = this.chartData?.labels || [];
                const index = Math.round(value);
                return labels[index] || `Période ${index + 1}`;
              },
            },
          },
        },
        interaction: {
          intersect: false,
        },
      };
    },
  },
  watch: {
    filters: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    camp_id: {
      handler() {
        this.fetchData();
      },
    },
    hasData() {
      if (this.hasData) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.calculateChartHeight();
          }, 150);
        });
      }
    },
    chartHeight() {
      // Forcer la mise à jour du graphique quand la hauteur change
      this.$nextTick(() => {
        if (this.$refs.chartContainer) {
          const chartInstance = this.$refs.chartContainer.querySelector('canvas')?.chart;
          if (chartInstance) {
            chartInstance.resize();
          }
        }
      });
    },
  },
  mounted() {
    this.fetchData();
    // Calculer la hauteur après que le DOM soit complètement rendu
    setTimeout(() => {
      this.calculateChartHeight();
    }, 100);
    window.addEventListener('resize', this.calculateChartHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateChartHeight);
  },
  methods: {
    buildApiFilters() {
      const activeFilters = this.filters || {};
      const niveau = activeFilters.niveau || {};

      return {
        campagne_id: this.camp_id || null,
        periode: {
          type: activeFilters.periode?.type || 'campagne',
          date_debut: activeFilters.periode?.date_debut || null,
          date_fin: activeFilters.periode?.date_fin || null,
        },
        ci_id: niveau.ci_id !== undefined && niveau.ci_id !== null ? niveau.ci_id : null,
        antenne_id: niveau.antenne_id !== undefined && niveau.antenne_id !== null ? niveau.antenne_id : null,
        groupement_id: niveau.groupement_id !== undefined && niveau.groupement_id !== null ? niveau.groupement_id : null,
        producteur_id: niveau.producteur_id !== undefined && niveau.producteur_id !== null ? niveau.producteur_id : null,
        cultures: (activeFilters.cultures || [])
          .map(id => {
            if (typeof id === 'number') {
              return id;
            }
            if (typeof id === 'object' && id !== null && id.id) {
              return typeof id.id === 'number' ? id.id : parseInt(id.id, 10);
            }
            if (typeof id === 'string') {
              const numId = parseInt(id, 10);
              return isNaN(numId) ? null : numId;
            }
            return id;
          })
          .filter(id => id !== null && id !== undefined && !isNaN(id)),
        certification: activeFilters.certification || 'toutes',
      };
    },
    async fetchData() {
      this.loading = true;
      try {
        const apiFilters = this.buildApiFilters();

        const response = await DashboardAPI.getEvolutionCertificationChart(apiFilters);
        if (response && response.data) {
          const labels = response.data.labels || [];
          const datasets = response.data.datasets || [];

          // Convertir les données en format BubbleChart
          // Format: { x: index, y: value, r: radius, label: period }
          const bubbleDatasets = datasets.map((dataset, datasetIndex) => {
            // Calculer la taille maximale pour normaliser les rayons
            const maxValue = Math.max(...dataset.data, 0);
            const minRadius = 5;
            const maxRadius = 30;

            const bubbleData = dataset.data.map((value, index) => {
              // Normaliser le rayon entre minRadius et maxRadius
              const normalizedRadius = maxValue > 0
                ? minRadius + ((value / maxValue) * (maxRadius - minRadius))
                : minRadius;

              return {
                x: index,
                y: value,
                r: normalizedRadius,
                label: labels[index] || `Période ${index + 1}`,
              };
            });

            return {
              label: dataset.label,
              data: bubbleData,
              backgroundColor: dataset.backgroundColor || dataset.borderColor || '#00ab53',
              borderColor: dataset.borderColor || '#00ab53',
              borderWidth: 2,
            };
          });

          this.chartData = {
            labels: labels,
            datasets: bubbleDatasets,
          };
        } else {
          this.chartData = {
            labels: [],
            datasets: [],
          };
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données d\'évolution de certification:', error);
        this.$message.error('Erreur lors du chargement des données d\'évolution de certification');
        this.chartData = {
          labels: [],
          datasets: [],
        };
      } finally {
        this.loading = false;
        // Recalculer la hauteur après le chargement des données
        this.$nextTick(() => {
          setTimeout(() => {
            this.calculateChartHeight();
          }, 50);
        });
      }
    },
    calculateChartHeight() {
      this.$nextTick(() => {
        setTimeout(() => {
          const parent = this.$el;
          if (!parent) {
            return;
          }

          const header = parent.querySelector('.chart-header');
          const headerHeight = header ? (header.offsetHeight || header.clientHeight || 50) : 50;

          // Utiliser getBoundingClientRect pour une mesure plus précise
          const parentRect = parent.getBoundingClientRect();
          let parentHeight = parentRect.height;

          // Si getBoundingClientRect ne donne pas de résultat, essayer d'autres méthodes
          if (!parentHeight || parentHeight === 0) {
            parentHeight = parent.offsetHeight || parent.clientHeight;
          }

          // Si toujours pas de hauteur, utiliser la hauteur du parent (el-card)
          if (!parentHeight || parentHeight === 0) {
            const cardParent = parent.closest('.el-card__body') || parent.closest('.chart-card');
            if (cardParent) {
              const cardRect = cardParent.getBoundingClientRect();
              parentHeight = cardRect.height || cardParent.offsetHeight || cardParent.clientHeight;
            }
          }

          const availableHeight = parentHeight - headerHeight;

          if (availableHeight > 0) {
            this.chartHeight = Math.max(availableHeight, 400);
          } else if (this.$refs.chartContainer) {
            const containerRect = this.$refs.chartContainer.getBoundingClientRect();
            const containerHeight = containerRect.height || this.$refs.chartContainer.offsetHeight || this.$refs.chartContainer.clientHeight;
            if (containerHeight > 0) {
              this.chartHeight = Math.max(containerHeight, 400);
            } else {
              // Fallback: utiliser une hauteur par défaut basée sur min-height
              this.chartHeight = 400;
            }
          } else {
            // Fallback final
            this.chartHeight = 400;
          }
        }, 150);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.certification-area-chart {
  width: 100%;
  height: 100%;
  min-height: 500px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  .chart-header {
    background-color: #00ab53;
    color: #fff;
    padding: 12px 16px;
    border-radius: 4px 4px 0 0;
    flex-shrink: 0;

    .chart-title {
      font-size: 16px;
      font-weight: bold;
      display: block;
    }
  }

  .chart-container {
    flex: 1 1 0;
    min-height: 400px;
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    ::v-deep > div {
      width: 100% !important;
      height: 100% !important;
      position: relative;
    }

    ::v-deep canvas {
      width: 100% !important;
      height: 100% !important;
      display: block;
    }

    ::v-deep .chartjs-render-monitor {
      width: 100% !important;
      height: 100% !important;
    }
  }

  .chart-loading,
  .chart-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #909399;

    i {
      font-size: 32px;
      color: #00ab53;
      animation: rotate 1s linear infinite;
    }

    p {
      margin-top: 16px;
      font-size: 14px;
    }
  }

  .chart-empty {
    i {
      color: #e6a23c;
      animation: none;
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

